import { onBeforeUnmount, ref } from "vue";

// Rounding in the two rects this compares, so a pixel of slack keeps an
// element resting exactly on its offset from reading as lifted off it.
const TOLERANCE = 1;

/**
 * Whether a `position: sticky` element has lifted off its resting place — the
 * one thing about a sticky element CSS cannot answer for itself, and what a
 * bar needs to know before it draws a surface over the content passing under
 * it.
 *
 * Both numbers are read at every check rather than once when watching starts:
 * the offset is usually a header height a stylesheet only holds as a default
 * until script measures the real header, and these apps mount before that
 * happens. Comparing live values costs a rect per frame of scrolling and owes
 * nothing to when anything settled.
 */
export function useStuck() {
  const isStuck = ref(false);
  let watched: HTMLElement | null = null;
  let frame = 0;

  function measure(): void {
    if (!watched) {
      return;
    }

    const offset = Number.parseFloat(getComputedStyle(watched).top);

    // `top: auto` — not sticky at this width, so it has no resting place to
    // lift off from.
    // The containing block, not the element: a stuck element reports the
    // offset it is held at, so its own rect says the same thing whether it has
    // lifted off or merely rests on that line — which is where a row directly
    // beneath the header sits from the first frame. Its parent keeps moving,
    // and a sticky element travels within it, so the parent having passed the
    // offset is what says there is now something underneath to cover.
    //
    // Rects only, no scroll position: which element scrolls differs by
    // template, and reading the wrong one would leave a bar with no surface
    // at all rather than one gained a moment early.
    const parent = watched.parentElement;

    isStuck.value =
      Number.isNaN(offset) || !parent
        ? false
        : parent.getBoundingClientRect().top < offset - TOLERANCE;
  }

  function onViewportChange(): void {
    if (frame) {
      return;
    }

    frame = requestAnimationFrame(() => {
      frame = 0;
      measure();
    });
  }

  function stopWatching(): void {
    // Capture, because a template whose scroller is an element rather than the
    // document gives no bubbling scroll event to listen for.
    document.removeEventListener("scroll", onViewportChange, true);
    window.removeEventListener("resize", onViewportChange);
    window.removeEventListener("load", onViewportChange);

    if (frame) {
      cancelAnimationFrame(frame);
      frame = 0;
    }

    watched = null;
    isStuck.value = false;
  }

  function watchStuck(el: HTMLElement | null | undefined): void {
    stopWatching();

    if (!el) {
      return;
    }

    watched = el;
    document.addEventListener("scroll", onViewportChange, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", onViewportChange, { passive: true });

    // Layout is not settled when these apps mount: the offset is a header
    // height a stylesheet holds as a default until script measures the real
    // one, and that lands after load. Without measuring again the first
    // verdict stands until something happens to be scrolled.
    window.addEventListener("load", onViewportChange, { passive: true });
    onViewportChange();
  }

  onBeforeUnmount(stopWatching);

  return { isStuck, stopWatching, watchStuck };
}
